import { useEffect, useMemo, useState } from "react";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Users,
  UserPlus,
  Pencil,
  Trash2,
  Search,
  Phone,
  MapPin,
  Save,
  X as XIcon,
  ChevronDown,
  ChevronUp,
  Star,
  Pause,
  History as HistoryIcon,
  Keyboard as KeyboardIcon,
} from "lucide-react";
import { useKeyboard } from "@/lib/KeyboardContext";
import AddressMapPreview from "@/components/AddressMapPreview";
import AllAddressesMapDialog from "@/components/AllAddressesMapDialog";

const EMPTY_ADDR = { street: "", street_no: "", area: "", floor: "", doorbell: "", instructions: "", is_default: false, status: "active" };

export function CustomerDialog({ open, onOpenChange, customer, onSaved, onChangeCustomer }) {
  // `customer` may be:
  //   • undefined/null  → fresh new card
  //   • { id, ... }     → edit existing card
  //   • { phones: [..] } (no id) → new card pre-seeded (e.g. delivery picker
  //     passes a phone string to bootstrap creation)
  const isEdit = !!customer?.id;
  const [name, setName] = useState("");
  const [phones, setPhones] = useState(["", "", "", ""]);
  const [profession, setProfession] = useState("");
  const [vatNo, setVatNo] = useState("");
  const [doy, setDoy] = useState("");
  const [addresses, setAddresses] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [allMapOpen, setAllMapOpen] = useState(false);
  const { open: vkOpen, toggle: vkToggle } = useKeyboard();

  useEffect(() => {
    if (open) {
      setName(customer?.name || "");
      const p = [...(customer?.phones || []), "", "", "", ""].slice(0, 4);
      setPhones(p);
      setProfession(customer?.profession || "");
      setVatNo(customer?.vat_no || "");
      setDoy(customer?.doy || "");
      // Migration: older customer cards have street/street_no on the
      // customer doc itself. Carry them to the first address row so the
      // operator still sees them — they'll re-save into the new shape.
      const existing = (customer?.addresses || []).map((a) => ({ ...a }));
      if (existing.length > 0 && !existing[0].street && customer?.street) {
        existing[0] = { ...existing[0], street: customer.street, street_no: customer.street_no || "" };
      } else if (existing.length === 0) {
        existing.push({ ...EMPTY_ADDR, street: customer?.street || "", street_no: customer?.street_no || "", is_default: true });
      }
      setAddresses(existing);
      setNote(customer?.note || "");
    }
  }, [open, customer]);

  const setPhone = (idx, v) => {
    const digits = v.replace(/\D/g, "").slice(0, 10);
    setPhones((arr) => arr.map((p, i) => (i === idx ? digits : p)));
  };
  const addAddress = () => setAddresses((a) => [...a, { ...EMPTY_ADDR }]);
  const updateAddress = (idx, patch) => {
    setAddresses((arr) => arr.map((a, i) => {
      if (i !== idx) return patch.is_default ? { ...a, is_default: false } : a;
      return { ...a, ...patch };
    }));
  };
  const removeAddress = (idx) => setAddresses((arr) => arr.filter((_, i) => i !== idx));

  const submit = async () => {
    if (!name.trim()) { toast.error("Όνομα υποχρεωτικό"); return; }
    if (name.length > 30) { toast.error("Όνομα μέχρι 30 χαρακτήρες"); return; }
    const ph = phones.filter((p) => p && p.length === 10);
    const badPh = phones.filter((p) => p && p.length !== 10);
    if (badPh.length) { toast.error("Όλα τα τηλέφωνα πρέπει να είναι 10 ψηφία"); return; }
    const addrs = addresses.filter((a) => a.street || a.area || a.floor || a.doorbell || a.instructions);
    const payload = {
      name: name.trim(), phones: ph, addresses: addrs, note: note || null,
      profession: profession || null,
      vat_no: vatNo || null,
      doy: doy || null,
      // Street/Street_no on the customer doc are no longer edited from
      // the UI; we null them so addresses become the single source.
      street: null,
      street_no: null,
    };
    setBusy(true);
    try {
      if (isEdit) {
        await http.put(`/customers/${customer.id}`, payload);
        toast.success("Ενημερώθηκε");
      } else {
        await http.post("/customers", payload);
        toast.success("Νέος πελάτης δημιουργήθηκε");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Σφάλμα");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-screen h-screen max-w-none max-h-none rounded-none p-0 gap-0 sm:rounded-none flex flex-col"
        data-testid="customer-dialog"
      >
        {/* Sticky header */}
        <DialogHeader className="px-4 py-2.5 border-b-2 border-slate-200 bg-white shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div>
              <DialogTitle className="text-xl">
                {isEdit ? `Επεξεργασία Πελάτη — Κωδ. ${customer?.code || "—"}` : "Νέος Πελάτης"}
              </DialogTitle>
              <DialogDescription className="text-xs">Στοιχεία επικοινωνίας, φορολογικά + διευθύνσεις παράδοσης.</DialogDescription>
            </div>
            <div className="flex items-center gap-2">
              {addresses.filter((a) => a && a.status !== "deleted" && (a.street || a.area || a.floor || a.doorbell || a.instructions)).length > 0 && (
                <Button
                  type="button"
                  size="lg"
                  variant="outline"
                  onClick={() => setAllMapOpen(true)}
                  data-testid="customer-form-show-all-map"
                  className="bg-blue-50 hover:bg-blue-100 border-blue-300 text-blue-800"
                  title="Όλες οι διευθύνσεις σε έναν μεγάλο χάρτη"
                >
                  <MapPin size={18} className="mr-1.5" /> Όλες στον χάρτη
                </Button>
              )}
              <Button
                type="button"
                size="lg"
                variant={vkOpen ? "default" : "outline"}
                onClick={vkToggle}
                data-testid="customer-form-kb-toggle"
                className={vkOpen ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                title="Πληκτρολόγιο οθόνης"
              >
                <KeyboardIcon size={18} className="mr-1.5" /> Πληκτρολόγιο
              </Button>
            </div>
          </div>
        </DialogHeader>

        {/* Scrollable body — desktop typically fits without scroll; mobile
            still scrolls. Increased grid density vs. the older 4-card
            vertical stack per the «χωρίς dropdown/scroll» UX brief. */}
        <div
          className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-slate-50"
          style={{ paddingBottom: vkOpen ? "360px" : "12px" }}
        >
          {/* Identity + Tax + Note --- single dense card on a 12-col grid */}
          <Card className="p-3">
            <div className="grid grid-cols-12 gap-x-3 gap-y-2 items-end">
              <div className="col-span-12 lg:col-span-4">
                <Label className="text-[11px] font-bold">Ονοματεπώνυμο (≤30)</Label>
                <Input
                  value={name}
                  maxLength={30}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="customer-form-name"
                  placeholder="π.χ. Γιώργος Παπαδημητρίου"
                  className="h-10 text-base"
                />
              </div>
              {phones.map((p, i) => (
                <div key={i} className="col-span-6 lg:col-span-4">
                  <Label className="text-[11px] font-bold flex items-center gap-1">
                    <Phone size={11} /> Τηλέφωνο {i + 1}
                  </Label>
                  <Input
                    value={p}
                    onChange={(e) => setPhone(i, e.target.value)}
                    placeholder="10 ψηφία"
                    maxLength={10}
                    inputMode="numeric"
                    data-testid={`customer-form-phone-${i}`}
                    className="font-mono h-10 text-base"
                  />
                </div>
              ))}
              <div className="col-span-12 lg:col-span-5">
                <Label className="text-[11px] font-bold">Επάγγελμα</Label>
                <Input value={profession} onChange={(e) => setProfession(e.target.value)} data-testid="customer-form-profession" className="h-10 text-base" />
              </div>
              <div className="col-span-4 lg:col-span-2">
                <Label className="text-[11px] font-bold">ΑΦΜ</Label>
                <Input value={vatNo} onChange={(e) => setVatNo(e.target.value.replace(/\D/g, "").slice(0, 9))} maxLength={9} inputMode="numeric" className="font-mono h-10 text-base" data-testid="customer-form-vat" />
              </div>
              <div className="col-span-8 lg:col-span-2">
                <Label className="text-[11px] font-bold">Δ.Ο.Υ.</Label>
                <Input value={doy} onChange={(e) => setDoy(e.target.value)} data-testid="customer-form-doy" className="h-10 text-base" />
              </div>
              <div className="col-span-12 lg:col-span-3">
                <Label className="text-[11px] font-bold">Σημείωση</Label>
                <Input value={note} onChange={(e) => setNote(e.target.value)} data-testid="customer-form-note" className="h-10 text-base" />
              </div>
            </div>
          </Card>

          {/* Addresses — 2-column responsive grid so the page no longer
              needs to scroll when the customer has 2-4 stored addresses. */}
          <Card className="p-3">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wide flex items-center gap-1">
                <MapPin size={13} /> Διευθύνσεις ({addresses.length})
              </h3>
              <Button size="sm" variant="outline" onClick={addAddress} data-testid="customer-form-add-address" className="h-9">
                + Διεύθυνση
              </Button>
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-2">
              {addresses.map((a, idx) => (
                <Card
                  key={idx}
                  className={`p-2.5 space-y-2 border-2 ${
                    a.status === "deleted"
                      ? "opacity-50 bg-red-50 border-red-200"
                      : a.status === "paused"
                      ? "bg-amber-50 border-amber-300"
                      : a.is_default
                      ? "bg-blue-50 border-blue-300"
                      : "border-slate-200"
                  }`}
                  data-testid={`customer-form-address-${idx}`}
                >
                  <div className="grid grid-cols-12 gap-2">
                    <div className="col-span-8">
                      <Label className="text-[10px] font-bold">Οδός</Label>
                      <Input
                        value={a.street || ""}
                        onChange={(e) => updateAddress(idx, { street: e.target.value })}
                        placeholder="π.χ. Πατησίων"
                        data-testid={`customer-form-addr-street-${idx}`}
                        className="h-9 text-sm"
                      />
                    </div>
                    <div className="col-span-4">
                      <Label className="text-[10px] font-bold">Αριθμός</Label>
                      <Input
                        value={a.street_no || ""}
                        onChange={(e) => updateAddress(idx, { street_no: e.target.value })}
                        maxLength={8}
                        placeholder="125Α"
                        data-testid={`customer-form-addr-street-no-${idx}`}
                        className="h-9 text-sm font-mono"
                      />
                    </div>
                    <div className="col-span-6">
                      <Label className="text-[10px] font-bold">Περιοχή</Label>
                      <Input value={a.area} onChange={(e) => updateAddress(idx, { area: e.target.value })} data-testid={`customer-form-addr-area-${idx}`} className="h-9 text-sm" />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-[10px] font-bold">Όροφος</Label>
                      <Input value={a.floor} onChange={(e) => updateAddress(idx, { floor: e.target.value })} className="h-9 text-sm" />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-[10px] font-bold">Κορδόνι</Label>
                      <Input value={a.doorbell} onChange={(e) => updateAddress(idx, { doorbell: e.target.value })} className="h-9 text-sm" />
                    </div>
                    <div className="col-span-12">
                      <Label className="text-[10px] font-bold">Οδηγίες</Label>
                      <Input value={a.instructions} onChange={(e) => updateAddress(idx, { instructions: e.target.value })} className="h-9 text-sm" />
                    </div>
                  </div>
                  {/* OpenStreetMap live preview (debounced 1.2s). Free,
                      no API key. Helps the operator catch typos in the
                      street name BEFORE saving. */}
                  <AddressMapPreview
                    street={a.street}
                    streetNo={a.street_no}
                    area={a.area}
                  />
                  <div className="flex items-center gap-3 text-xs pt-1 border-t border-slate-200">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <Switch
                        checked={a.is_default}
                        onCheckedChange={(v) => updateAddress(idx, { is_default: v })}
                        data-testid={`customer-form-addr-default-${idx}`}
                      />
                      <Star size={12} className="text-yellow-500" /> Default
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <Switch
                        checked={a.status === "paused"}
                        onCheckedChange={(v) => updateAddress(idx, { status: v ? "paused" : "active" })}
                        data-testid={`customer-form-addr-paused-${idx}`}
                      />
                      <Pause size={12} className="text-amber-600" /> Παύση
                    </label>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="ml-auto text-red-600 hover:bg-red-50 h-8"
                      onClick={() => removeAddress(idx)}
                      data-testid={`customer-form-addr-delete-${idx}`}
                    >
                      <Trash2 size={12} className="mr-1" /> Διαγραφή
                    </Button>
                  </div>
                </Card>
              ))}
              {addresses.length === 0 && (
                <p className="col-span-full text-sm text-slate-400 italic text-center py-4">
                  Δεν υπάρχουν διευθύνσεις. Πάτησε «+ Διεύθυνση».
                </p>
              )}
            </div>
          </Card>
        </div>

        {/* Sticky footer */}
        <DialogFooter className="px-4 py-2.5 border-t-2 border-slate-200 bg-white shrink-0">
          {onChangeCustomer && (
            <Button
              size="lg"
              variant="outline"
              onClick={onChangeCustomer}
              className="mr-auto border-2 border-blue-400 text-blue-700 hover:bg-blue-50 font-bold"
              data-testid="customer-form-change-customer"
            >
              <UserPlus size={16} className="mr-1.5" /> Αλλαγή Πελάτη
            </Button>
          )}
          <Button size="lg" variant="outline" onClick={() => onOpenChange(false)} data-testid="customer-form-cancel">
            <XIcon size={16} className="mr-1.5" /> Άκυρο
          </Button>
          <Button
            size="lg"
            onClick={submit}
            disabled={busy}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="customer-form-save"
          >
            <Save size={16} className="mr-1.5" /> {isEdit ? "Αποθήκευση" : "Δημιουργία"}
          </Button>
        </DialogFooter>
      </DialogContent>
      {allMapOpen && (
        <AllAddressesMapDialog
          open={allMapOpen}
          onClose={() => setAllMapOpen(false)}
          customerName={name}
          addresses={addresses}
        />
      )}
    </Dialog>
  );
}

function CustomerHistoryDialog({ customer, onOpenChange }) {
  const [data, setData] = useState(null);
  const [topItems, setTopItems] = useState(null);
  useEffect(() => {
    if (!customer) return;
    http.get(`/customers/${customer.id}/history`).then((r) => setData(r.data)).catch(() => setData({ items: [], total: 0 }));
    http.get(`/customers/${customer.id}/top-items?limit=10`).then((r) => setTopItems(r.data)).catch(() => setTopItems({ items: [] }));
  }, [customer]);
  if (!customer) return null;
  const c = data?.customer || customer;
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="customer-history-dialog">
        <DialogHeader>
          <DialogTitle>Ιστορικό — {customer.name}</DialogTitle>
          <DialogDescription>Πωλήσεις & στατιστικά πελάτη.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-3 mb-4">
          <Card className="p-3"><div className="text-xs text-slate-500">Παραγγελίες</div><div className="text-xl font-bold">{c.orders_count || 0}</div></Card>
          <Card className="p-3"><div className="text-xs text-slate-500">Συνολικό ποσό</div><div className="text-xl font-bold text-emerald-700">{(c.total_spent || 0).toFixed(2)}€</div></Card>
          <Card className="p-3"><div className="text-xs text-slate-500">Εγγραφές</div><div className="text-xl font-bold">{data?.total || 0}</div></Card>
        </div>
        <Tabs defaultValue="history" data-testid="customer-history-tabs">
          <TabsList className="grid grid-cols-2 w-full mb-3">
            <TabsTrigger value="history" data-testid="tab-history">Ιστορικό</TabsTrigger>
            <TabsTrigger value="top" data-testid="tab-top-items">Top Προϊόντα</TabsTrigger>
          </TabsList>
          <TabsContent value="history">
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-2">Ημ/νία</th>
                    <th className="text-left px-3 py-2">Είδος</th>
                    <th className="text-right px-3 py-2">Ποσό</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.items || []).map((it, i) => {
                    const srcLabel = {
                      "retail_non_fiscal": "Λιανική (χωρίς απόδ.)",
                      "retail": "Λιανική",
                      "delivery": "Delivery",
                      "restaurant": "Εστιατόριο",
                      "fiscal_receipt": "Απόδειξη",
                      "fiscal_receipt_legacy": "Απόδειξη (παλιά)",
                    }[it.source] || it.type || it.source || "—";
                    return (
                      <tr key={i} className="border-t align-top">
                        <td className="px-3 py-2 font-mono text-xs">
                          {(it.created_at || it.sent_at || "").slice(0, 19).replace("T", " ")}
                        </td>
                        <td className="px-3 py-2">
                          <div className="text-sm">{srcLabel}</div>
                          {(it.items || []).length > 0 && (
                            <div className="text-[11px] text-slate-500 mt-0.5">
                              {(it.items || []).map((li, li_idx) => (
                                <span key={li_idx}>
                                  {li_idx > 0 && " · "}
                                  {li.qty} × {li.description}
                                </span>
                              ))}
                            </div>
                          )}
                          {it.user_name && (
                            <div className="text-[10px] text-slate-400 mt-0.5">
                              Χειριστής: {it.user_name}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right font-mono">
                          {Number(it.total_amount || 0).toFixed(2)}€
                        </td>
                      </tr>
                    );
                  })}
                  {(!data?.items || data.items.length === 0) && (
                    <tr><td colSpan={3} className="text-center py-6 text-slate-400">Δεν υπάρχει ακόμη ιστορικό.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>
          <TabsContent value="top">
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-2 w-12">#</th>
                    <th className="text-left px-3 py-2">Προϊόν</th>
                    <th className="text-right px-3 py-2">Τεμάχια</th>
                    <th className="text-right px-3 py-2">Παραγγ.</th>
                    <th className="text-right px-3 py-2">Score</th>
                  </tr>
                </thead>
                <tbody>
                  {(topItems?.items || []).map((it, i) => {
                    const st = it._stats || {};
                    return (
                      <tr key={i} className="border-t" data-testid={`history-top-row-${i}`}>
                        <td className="px-3 py-2 font-mono text-xs text-purple-700 font-bold">{i + 1}</td>
                        <td className="px-3 py-2 font-bold">{it.description}</td>
                        <td className="px-3 py-2 text-right font-mono">×{st.total_qty || 0}</td>
                        <td className="px-3 py-2 text-right font-mono">{st.frequency || 0}</td>
                        <td className="px-3 py-2 text-right font-mono text-purple-700 font-bold">{Number(st.score || 0).toFixed(0)}</td>
                      </tr>
                    );
                  })}
                  {(!topItems?.items || topItems.items.length === 0) && (
                    <tr><td colSpan={5} className="text-center py-6 text-slate-400">Δεν υπάρχουν ακόμη συχνά προϊόντα.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-400 mt-2 italic">
              Score = τεμάχια × συχνότητα. Όσο μεγαλύτερο το score, τόσο πιο «αγαπημένο».
            </p>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

export default function CustomersPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [sort, setSort] = useState("-updated_at");
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [delConfirm, setDelConfirm] = useState(null);
  const [history, setHistory] = useState(null);
  const load = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams({ limit: "200", sort });
      if (q.trim()) params.set("q", q.trim());
      const r = await http.get(`/customers?${params}`);
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error("Σφάλμα φόρτωσης πελατών");
    } finally { setBusy(false); }
  };
  useEffect(() => { load();   }, [sort]);
  useEffect(() => {
    const t = setTimeout(load, 350);
    return () => clearTimeout(t);
     
  }, [q]);

  const handleDelete = async () => {
    if (!delConfirm) return;
    setBusy(true);
    try {
      await http.delete(`/customers/${delConfirm.id}`);
      toast.success("Διαγράφηκε");
      setDelConfirm(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Σφάλμα");
    } finally { setBusy(false); }
  };

  const toggleSort = (key) => {
    setSort((s) => s === key ? `-${key}` : key);
  };
  const sortArrow = (key) => (sort === key ? <ChevronUp size={12} className="inline" /> : sort === `-${key}` ? <ChevronDown size={12} className="inline" /> : null);

  return (
    <div className="p-6 space-y-4 max-w-[1800px] mx-auto" data-testid="customers-page">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Users size={24} className="text-blue-600" />
          <h2 className="text-2xl font-bold">Πελάτες ({total})</h2>
        </div>
        <Button onClick={() => { setEditing(null); setDialogOpen(true); }} className="bg-emerald-600 hover:bg-emerald-700" data-testid="customers-create-btn">
          <UserPlus size={16} className="mr-1.5" /> Νέος Πελάτης
        </Button>
      </div>

      <Card className="p-3">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Αναζήτηση: κωδικός, τηλέφωνο, όνομα, ΑΦΜ…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9"
            data-testid="customers-search"
          />
        </div>
      </Card>

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-left">
            <tr>
              <th className="px-3 py-2 cursor-pointer" onClick={() => toggleSort("code")} data-testid="customers-sort-code">Κωδικός {sortArrow("code")}</th>
              <th className="px-3 py-2 cursor-pointer" onClick={() => toggleSort("name")} data-testid="customers-sort-name">Ονοματεπώνυμο {sortArrow("name")}</th>
              <th className="px-3 py-2">Τηλέφωνα</th>
              <th className="px-3 py-2">ΑΦΜ</th>
              <th className="px-3 py-2">Διευθύνσεις</th>
              <th className="px-3 py-2 cursor-pointer text-right" onClick={() => toggleSort("total_spent")}>Σύνολο {sortArrow("total_spent")}</th>
              <th className="px-3 py-2 cursor-pointer text-right" onClick={() => toggleSort("updated_at")}>Ενημ. {sortArrow("updated_at")}</th>
              <th className="px-3 py-2 text-right w-44">Ενέργειες</th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} className="border-t hover:bg-slate-50" data-testid={`customer-row-${c.id}`}>
                <td className="px-3 py-2 font-mono text-xs text-blue-700">{c.code || "—"}</td>
                <td className="px-3 py-2 font-medium">{c.name}</td>
                <td className="px-3 py-2 font-mono text-xs">{(c.phones || []).join(" · ")}</td>
                <td className="px-3 py-2 font-mono text-xs">{c.vat_no || "—"}</td>
                <td className="px-3 py-2 text-xs text-slate-600">
                  {(c.addresses || []).filter((a) => a.status === "active").slice(0, 2).map((a, i, arr) => {
                    const parts = [a.street && `${a.street}${a.street_no ? " " + a.street_no : ""}`, a.area].filter(Boolean).join(", ");
                    return (
                      <span key={i}>{parts || "—"}{i === 0 && arr.length > 1 ? " · " : ""}</span>
                    );
                  })}
                </td>
                <td className="px-3 py-2 text-right font-semibold text-emerald-700">{(c.total_spent || 0).toFixed(2)}€</td>
                <td className="px-3 py-2 text-right text-xs text-slate-500">{(c.updated_at || "").slice(0, 10)}</td>
                <td className="px-3 py-2 text-right space-x-1">
                  <Button variant="outline" size="sm" onClick={() => setHistory(c)} data-testid={`customer-history-${c.id}`}>
                    <HistoryIcon size={14} />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => { setEditing(c); setDialogOpen(true); }} data-testid={`customer-edit-${c.id}`}>
                    <Pencil size={14} />
                  </Button>
                  <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => setDelConfirm(c)} data-testid={`customer-delete-${c.id}`}>
                    <Trash2 size={14} />
                  </Button>
                </td>
              </tr>
            ))}
            {items.length === 0 && !busy && (
              <tr><td colSpan={8} className="text-center py-12 text-slate-400">{q ? "Δεν βρέθηκαν πελάτες." : "Δεν υπάρχουν ακόμη πελάτες."}</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      <CustomerDialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) setEditing(null); }} customer={editing} onSaved={load} />

      <Dialog open={!!delConfirm} onOpenChange={(o) => !o && setDelConfirm(null)}>
        <DialogContent data-testid="customer-delete-dialog">
          <DialogHeader>
            <DialogTitle>Διαγραφή Πελάτη</DialogTitle>
            <DialogDescription>Θα διαγραφεί ο πελάτης «{delConfirm?.name}». Μη επιστρέψιμη ενέργεια.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelConfirm(null)}>Άκυρο</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={handleDelete} data-testid="customer-delete-confirm">
              <Trash2 size={14} className="mr-1" /> Διαγραφή
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {history && <CustomerHistoryDialog customer={history} onOpenChange={(o) => !o && setHistory(null)} />}
    </div>
  );
}
